import { difference } from 'lodash-es'
import i18n, { ParseKeys } from 'i18next'
import { LocalDate } from '@js-joda/core'
import { fromNumberString } from '@migrate/utils/number'
import { stores } from '@migrate/stores'
import { LocalesStore } from '@migrate/stores/LocalesStore'
import parse from 'date-fns/parse'
import parseIso from 'date-fns/parse'
import { UserBasicAndTimestamp } from '@portal/pkg-api/gen/navigator'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { LinkOptions } from '@tanstack/react-router'

export const MIN_YEAR = 1901
export const MAX_YEAR = 2199
const ISO_DATE_STRING_FORMAT = 'yyyy-MM-dd'
export const MIN_DATE = new Date(MIN_YEAR, 0, 1)
export const MAX_DATE = new Date(MAX_YEAR, 11, 31)

export interface IFormFieldRenderer {
  render(field: IFormField): string
}

export enum FieldInputType {
  Text,
  TextArea,
  Date,
  Select,
  MultiSelect,
  Header,
  NamedRange,
  Union,
  Image,
  Attachment,
  TextWithSelect,
  RichText,
  ParentEntityType,
  EntityRef,
  InfoList,
  MultiUser,
  Url,
}

export enum IFormFieldMode {
  ReadOnly,
  ClickToEdit,
  Edit,
}

export enum FieldDataType {
  String,
  Numeric,
  Date,
  Array,
}

export interface IFormField {
  id?: number
  name: string
  label: string
  value: any
  meta: IFormFieldMeta
  state: IFormFieldState
}

export interface IFormFieldState {
  editing: boolean // setting this to true allows inline change saves on click outside
  hidden: boolean
  filtered: boolean
  helperText: string
  error: boolean
  errorText: string
  startEditValue?: any
  editValue?: any
  startEditSelectValue?: string
}

export interface IFormFieldMeta {
  type: FieldDataType
  control: FieldInputType
  mode?: IFormFieldMode
  value2?: string
  renderer?: IFormFieldRenderer
  format?: string
  fractionDigits?: number
  min?: number | null
  max?: number | null
  supportsMagnitudeSuffix?: boolean // accept `m`/`b` shorthand on numeric & currency input
  maxLength?: number
  maxLengthCounter?: boolean // true add a counter of length compared to max
  minDate?: LocalDate
  maxDate?: LocalDate
  dateFormat?: string
  disabled?: boolean
  required?: boolean
  unit?: string
  selectMeta?: ISelectMeta
  options?: ISelectOption[]
  conditionalSelects?: IConditionalSelect[]
  conditionalHiddens?: IConditionalHidden[]
  model?: any
  labelButton?: ILabelButtons
  lastWritten?: UserBasicAndTimestamp
  namedRangeId?: number
  rowIndex?: number
  rowName?: string
  colName?: string
  showFullLength?: boolean // display override on style to show long text
  showActions?: boolean // to show action menu for assumptions
  disableUnderline?: boolean // to hide the underline in material UI
  variant?: string // display variant
  customControl?: string // display custom control type
  fieldMargin?: 'dense' | 'none' | 'normal' // override the field margin
  additionalProps?: any
  autoFocus?: boolean
  keepFocusOnError?: boolean
  bottomAdornments?: IFormFieldAdornment[]
  inputAdornmentProvider?: (value: any) => IFormFieldAdornment[] | null
  suspendExitFullScreen?: boolean
  entityTypeId?: number
  optionProvider?: (field: IFormField, search: string, meta?: any) => any
  renderOptionProvider?: (params: any, option: any) => any
  labelProvider?: (value: any) => any
  alphabetize?: boolean
}

export interface IFormFieldAdornment {
  id: string
  icon: IconDefinition
  labelKey?: ParseKeys
  tooltipKey?: ParseKeys
  link?: LinkOptions
}

export interface ISelectMeta {
  clearable?: boolean
}

export interface ISelectOption {
  value: string
  label: string
  hidden: boolean
  disable?: boolean
  group?: string
  model?: any
}

export interface IConditionalSelect {
  childField: string
  valueMap: any
}

export interface IConditionalHidden {
  value: string
  hiddenNames: string[]
}

export interface ILabelButtons {
  info?: boolean
  document?: boolean
}

export function createFormField(
  name: string,
  type?: FieldDataType,
  control?: FieldInputType,
): IFormField {
  return {
    name,
    label: '',
    value: null,
    state: {
      helperText: '',
      error: false,
      errorText: '',
      hidden: false,
      filtered: false,
      editing: false,
    },
    meta: {
      type: type ? type : FieldDataType.String,
      control: control ? control : FieldInputType.Text,
      mode: IFormFieldMode.Edit,
      required: true,
    },
  }
}

export function labelValueField(
  label: string,
  value: any,
  renderer?: IFormFieldRenderer,
  disabled?: boolean,
  disableUnderline?: boolean,
) {
  const field = createFormField(label)
  field.label = label
  field.value = value
  field.meta.mode = IFormFieldMode.ReadOnly
  field.meta.required = false
  field.meta.disabled = disabled ? disabled : false
  field.meta.disableUnderline = disableUnderline ? disableUnderline : false

  if (renderer) {
    field.meta.renderer = renderer
  }

  return field
}

export function convertFieldValue(field: IFormField) {
  let v = field.value
  const type = field.meta.type
  const control = field.meta.control

  if (!v) {
    return
  }

  if (type === FieldDataType.Array) {
    v = v.join ? v.join(', ') : v
  }

  if (type === FieldDataType.Date) {
    v = v.toString()
  }

  if (type === FieldDataType.Numeric) {
    // FIXME: Inconsistent value type here, expected to be a string but sometimes is a number...
    v = '' + fromNumberString(v.toString()).toNumber()
  }

  if (control === FieldInputType.Header) {
    v = JSON.stringify(v.split(',').map((item: string) => item.trim()))
  }

  return v
}

// Needed to parse field values back into 'desired' shape after updating their values
export function convertFieldValueFromServer(field: IFormField, value: string | undefined) {
  switch (field.meta.control) {
    case FieldInputType.Header:
      return parseStringifyObjectValue(field.value)
  }
  return value
}

export function parseStringifyObjectValue(value: string | undefined) {
  if (!value) {
    return null
  }
  try {
    JSON.parse(value)
    return JSON.parse(value).toString()
  } catch {
    return value
  }
}

export function validateFormField(field: IFormField) {
  let v = field.value
  const type = field.meta.type
  const control = field.meta.control
  const { editValue } = field.state
  field.state.error = false
  field.state.errorText = ''

  if (type === FieldDataType.Array && v !== null) {
    v = v.slice()
  }

  if (field.meta.required && (!v || v.length === 0)) {
    field.state.error = true
    field.state.errorText = i18n.t('validation.required')
    return false
  }

  if (control === FieldInputType.MultiUser && v !== null) {
    if (v.split(/,\s*/).length > 255) {
      field.state.error = true
      field.state.errorText = i18n.t('validation.multiUserMax', { value: 255 })
    }
  }

  if (control === FieldInputType.Attachment && v !== null) {
    v = JSON.parse(v)

    if (field.state.editing) {
      field.state.error = true
      field.state.errorText = i18n.t('validation.uploading')
      return false
    }
  }

  if (type === FieldDataType.Date && (v || editValue)) {
    if (!(v instanceof LocalDate)) {
      // if not a local date, but a string ensure it can be converted to local date
      if (!isValidDate(v)) {
        field.state.error = true
        field.state.errorText = i18n.t('validation.notDate')
        return false
      }
    }

    if (field.meta.minDate) {
      if (
        (editValue && editValue instanceof LocalDate && editValue.isBefore(field.meta.minDate)) ||
        (v instanceof LocalDate && v.isBefore(field.meta.minDate))
      ) {
        field.state.error = true
        field.state.errorText = i18n.t('validation.notDate')
        return false
      }
    }

    if (field.meta.maxDate) {
      if (
        (editValue && editValue instanceof LocalDate && editValue.isAfter(field.meta.maxDate)) ||
        (v instanceof LocalDate && v.isAfter(field.meta.maxDate))
      ) {
        field.state.error = true
        field.state.errorText = i18n.t('validation.notDate')
        return false
      }
    }
  }

  if (
    field.meta.maxLength &&
    field.meta.maxLength > 0 &&
    v !== undefined &&
    v !== null &&
    v !== ''
  ) {
    if (v.length > field.meta.maxLength) {
      field.state.error = true
      field.state.errorText = i18n.t('validation.maxLength', { value: field.meta.maxLength })
      return false
    }
  }

  if (type === FieldDataType.Numeric && v !== undefined && v !== null && v !== '') {
    const converter = fromNumberString(v.toString())
    if (isNaN(v) || !converter.isValid()) {
      field.state.error = true
      field.state.errorText = i18n.t('validation.notNumber')
      return false
    } else {
      const numberValue = converter.toNumber()
      if (
        field.meta!.min !== null &&
        field.meta!.min !== undefined &&
        numberValue! < field.meta!.min
      ) {
        field.state.error = true
        field.state.errorText = i18n.t('validation.min', { value: field.meta!.min })
        return false
      }

      if (
        field.meta!.max !== null &&
        field.meta!.max !== undefined &&
        numberValue! > field.meta!.max
      ) {
        field.state.error = true
        field.state.errorText = i18n.t('validation.max', { value: field.meta!.max })
        return false
      }
    }
  }

  // make sure only valid non-empty options are chosen for multi-select types
  if (
    v !== undefined &&
    v !== null &&
    v !== '' &&
    field.meta.control === FieldInputType.MultiSelect
  ) {
    if (field.meta.options) {
      const optionValues: string[] = field.meta.options.map((option) => option.value)
      if (difference(v, optionValues).length === 0) {
        return true
      }
    }
    field.state.error = true
    field.state.errorText = i18n.t('validation.notOption')
    return false
  }

  // make sure only valid non-empty options are chosen for select types
  if (v !== undefined && v !== null && v !== '' && field.meta.control === FieldInputType.Select) {
    if (
      field.meta.options &&
      field.meta.options.findIndex((option: ISelectOption) => {
        return option.value === field.value
      }) >= 0
    ) {
      return true
    }
    field.state.error = true
    field.state.errorText = i18n.t('validation.notOption')
    return false
  }

  return true
}

export function isValidDate(value: string) {
  const { dateFormatLocale } = stores[LocalesStore.STORE_NAME]
  try {
    const dateToCheck = parse(value, dateFormatLocale, new Date())
    const dateToCheckIso = parseIso(value, ISO_DATE_STRING_FORMAT, new Date())

    return (
      (dateToCheck && !isNaN(dateToCheck.getTime())) ||
      (dateToCheckIso && !isNaN(dateToCheckIso.getTime()))
    )
  } catch (e) {
    console.log(e)
    return false
  }
}

export function getFractionDigits(field: IFormField) {
  return getFractionDigitsFromFormat(field.meta.format ?? '')
}

export function getFractionDigitsFromFormat(metaFormat: string | undefined) {
  let fractionDigits
  if (metaFormat) {
    const formatArr = metaFormat.split('.')
    fractionDigits = formatArr.length > 1 ? formatArr[1].length : 0
  }
  return fractionDigits
}

export function validateBetweenFields(field1: IFormField, field2: IFormField, showOn: IFormField) {
  // ensure both fields are valid then compare them
  if (validateFormField(field1) && validateFormField(field2)) {
    const v1 = field1.value
    const type1 = field1.meta.type
    const v2 = field2.value
    const type2 = field2.meta.type

    // between numbers
    if (type1 === FieldDataType.Numeric && type1 === type2) {
      if (parseFloat(v2) <= parseFloat(v1)) {
        if (showOn === field1) {
          field1.state.error = true
          field1.state.errorText = i18n.t('validation.notBetweenLess', { value: v2 })
        } else {
          field2.state.error = true
          field2.state.errorText = i18n.t('validation.notBetweenMore', { value: v1 })
        }
      }
    }

    // between dates
    if (type1 === FieldDataType.Date && type1 === type2) {
      if (new Date(v2) <= new Date(v1)) {
        if (showOn === field1) {
          field1.state.error = true
          field1.state.errorText = i18n.t('validation.notBefore', { value: v2 })
        } else {
          field2.state.error = true
          field2.state.errorText = i18n.t('validation.notAfter', { value: v1 })
        }
      }
    }
  }
  return true
}

/**
 * Resolves the entity type id of the entity an entity-ref field refers to.
 *
 * The assumption model is the source of truth: `entityRefEntityTypeId` may sit
 * directly on the model or nested under its `assumption`. `meta.entityTypeId` is
 * only a fallback because it can be overwritten with the *owning* entity's type
 * (e.g. AssumptionControl sets it to the host entity), so it must be consulted
 * last and only when the model carries no reference type.
 */
export function getEntityRefEntityTypeId(field: IFormField): number | undefined {
  return (
    field.meta.model?.entityRefEntityTypeId ??
    field.meta.model?.assumption?.entityRefEntityTypeId ??
    field.meta.entityTypeId
  )
}
